import { useState } from 'react'
import { Link } from 'react-router-dom'
import { support } from '../lib/supportApi'
import { Button, Field } from './ui'

export const RECEIPT_ACCEPT = '.png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf'
export function receiptError(file: File | null): string {
  if (!file) return ''
  if (!file.size) return 'This file is empty. Choose another receipt.'
  if (file.size > 10 * 1024 * 1024) return 'Choose a receipt smaller than 10 MB.'
  if (!/\.(png|jpe?g|pdf)$/i.test(file.name)) return 'Choose a PNG, JPG, or PDF receipt.'
  return ''
}

/** A retry keeps the created conversation; a failed upload never looks sent. */
export default function ReceiptForm({ initialAmount = '' }: { initialAmount?: string }) {
  const [amount, setAmount] = useState(initialAmount)
  const [reference, setReference] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [ticket, setTicket] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const invalid = receiptError(file)
  if (sent) return <div className="stack" role="status">
    <strong>Deposit submitted for review</strong>
    <p className="muted">Support will verify your deposit before adding Birr to your wallet.</p>
    <Link className="btn" to={`/support?ticket=${ticket}`}>View receipt and conversation</Link>
    <Button variant="ghost" onClick={() => { setSent(false); setTicket(''); setFile(null); setReference(''); setAmount('') }}>Submit another deposit</Button>
  </div>
  return <form className="stack" onSubmit={async e => {
    e.preventDefault()
    if (busy || invalid || (!file && !reference.trim())) return
    setBusy(true); setError('')
    try {
      let id = ticket
      if (!id) {
        const result = await support.create({ category: 'bank_transfer', subject: 'CBE deposit verification',
          message: `Please verify my CBE deposit of ${amount} ETB.`, transfer_amount: amount,
          transfer_reference: reference.trim() || null })
        id = result.id; setTicket(id)
      }
      if (file) await support.upload(id, file)
      setSent(true)
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not send your receipt. Please retry.') }
    finally { setBusy(false) }
  }}>
    <div className="field-row">
      <Field label="Amount deposited (ETB)" htmlFor="receipt-amount"><input id="receipt-amount" type="number" min="0.01" max="999999999" step="0.01" required value={amount} disabled={busy || !!ticket} onChange={e => setAmount(e.target.value)} /></Field>
      <Field label="Transaction reference (optional with receipt)" htmlFor="receipt-reference"><input id="receipt-reference" maxLength={128} value={reference} disabled={busy || !!ticket} onChange={e => setReference(e.target.value)} /></Field>
    </div>
    <Field label="Receipt image or PDF" htmlFor="receipt-file"><input id="receipt-file" key={file ? "selected" : "empty"} type="file" accept={RECEIPT_ACCEPT} disabled={busy} onChange={e => setFile(e.target.files?.[0] ?? null)} /></Field>
    <p className="tiny muted">Attach a PNG, JPG, or PDF up to 10 MB, or enter your transaction reference.</p>
    {file && <div className="row"><span>{file.name} · {(file.size / 1024).toFixed(0)} KB</span><Button type="button" variant="ghost" disabled={busy} onClick={() => setFile(null)}>Remove attachment</Button></div>}
    {(error || invalid) && <p className="alert error" role="alert">{invalid || error}</p>}
    {ticket && <p className="tiny muted">Your conversation is saved. Retry to finish sending the attachment, or <Link to={`/support?ticket=${ticket}`}>open Support</Link>.</p>}
    <Button type="submit" variant="primary" loading={busy} disabled={!!invalid || (!file && !reference.trim())}>{busy ? 'Sending receipt…' : ticket ? 'Retry submission' : 'Submit deposit'}</Button>
  </form>
}
